import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import { Voyageurs } from '../models/Voyageurs';
import { API_BASE_URL } from '../config/api';
import { isLegrandUser } from '../utils/userUtils';
import FileDropZone from '../components/commun/FileDropZone';
import ConditionsGeneralesVente from '../components/commun/ConditionsGeneralesVente';

interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite: string;
  informations: string;
  commentaires?: string;
}

const BUTS_VOYAGE = ['Affaires', 'Tourisme', 'Autre'];

const FormSingapourECard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const demandeId = state.demandeId;
  const isEditMode = demandeId != null;

  // États issus des paramètres de route
  const title = state?.title || '';
  const prixconsulat = state?.prixconsulat || 0;
  const prixintervention = state?.prixintervention || 0;
  const urlFlag = state?.url_flag || '';
  const validite = state?.validite || '';
  const informations = state?.informations || '';
  const commentaires = state?.commentaires || '';

  // Champs spécifiques à l'e-Card Singapour
  const [dateArrivee, setDateArrivee] = useState<string>('');
  const [dateRetour, setDateRetour] = useState<string>('');
  const [butVoyage, setButVoyage] = useState<string>('');
  const [butVoyagePrecision, setButVoyagePrecision] = useState<string>('');
  const [billetAvionFile, setBilletAvionFile] = useState<File | null>(null);
  const [reservationHotelFile, setReservationHotelFile] = useState<File | null>(null);

  // Champs communs à tous les formulaires
  const [voyageurs, setVoyageurs] = useState<Voyageurs[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hideFeesForLegrand] = useState<boolean>(isLegrandUser());

  // Type d'utilisateur et écran de succès
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  const total = prixconsulat + prixintervention;

  // Validation du formulaire
  const validateForm = () => {
    const parseDate = (dateStr: string): Date | null => {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      }
      return null;
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const arriveeDate = parseDate(dateArrivee);
    const retourDate = parseDate(dateRetour);

    const errors: string[] = [];

    if (arriveeDate) {
      if (isNaN(arriveeDate.getTime()) || arriveeDate <= today) {
        errors.push("La date d'arrivée doit être une date valide et postérieure à aujourd'hui.");
      }
    } else {
      errors.push("La date d'arrivée à Singapour est requise.");
    }

    if (dateRetour && retourDate) {
      if (isNaN(retourDate.getTime()) || (arriveeDate && retourDate <= arriveeDate)) {
        errors.push("La date de retour doit être une date valide et postérieure à la date d'arrivée.");
      }
    } else if (dateRetour && !retourDate) {
      errors.push('La date de retour est requise et doit être au format valide.');
    }

    if (!selectedVoyageur) errors.push('Voyageur non sélectionné');
    if (!dateArrivee.trim()) errors.push("Date d'arrivée à Singapour");
    if (!dateRetour.trim()) errors.push('Date de retour de Singapour');
    if (!butVoyage.trim()) errors.push('But du voyage');
    if (butVoyage === 'Autre' && !butVoyagePrecision.trim()) {
      errors.push('Précision du but du voyage');
    }
    if (!billetAvionFile) errors.push("Billet d'avion");
    if (!reservationHotelFile) errors.push("Réservation d'hôtel");
    if (!sliderValue) errors.push('Engagement sur les conditions générales de vente');

    setChampsNonRemplis(errors);
    setIsFormValid(errors.length === 0);
  };

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateArrivee,
    dateRetour,
    butVoyage,
    butVoyagePrecision,
    billetAvionFile,
    reservationHotelFile,
    selectedVoyageur,
    sliderValue,
  ]);

  // Récupération de la liste des voyageurs
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        // Sélection automatique s'il n'y a qu'un seul voyageur
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        } else {
          setSelectedVoyageur('');
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true); // Par défaut, considérer comme société
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find(v => v.id.toString() === selectedVoyageur);

  // Empêcher la fermeture de l'onglet sans confirmation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Bouton retour
  const handleBack = () => {
    if (
      window.confirm(
        'Les informations saisies ne sont pas enregistrées. Êtes-vous sûr de quitter ce formulaire ?'
      )
    ) {
      navigate(-1);
    }
  };

  // Formatage de la date (jj/mm/aaaa)
  const formatDate = (input: string) => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  // Soumission
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId: string | null = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }

      const etatFormulaire = champsNonRemplis.length === 0;

      const formData = new FormData();
      formData.append('etatFormulaire', String(etatFormulaire));
      formData.append('idUtilisateur', userId || '');
      formData.append('idVoyageur', selectedVoyageur);
      formData.append('titreVisa', title);
      // `dateDepart` alimente la colonne date_depart de la table demandes, sur
      // laquelle l'admin trie et affiche les dossiers : pour une e-Card, la date
      // qui fait foi est celle de l'arrivée dans le pays.
      formData.append('dateDepart', dateArrivee);
      formData.append('dateSortie', dateRetour);
      formData.append('dateArriveeSingapour', dateArrivee);
      formData.append('dateRetourSingapour', dateRetour);
      formData.append('validite', validite);
      formData.append('assuranceVisa', 'non');
      formData.append('prixconsulat', String(prixconsulat));
      formData.append('prixintervention', String(prixintervention));
      formData.append('butVoyage', butVoyage);
      if (butVoyage === 'Autre') {
        formData.append('butVoyagePrecision', butVoyagePrecision);
      }
      if (billetAvionFile) {
        formData.append('billetAvion', billetAvionFile, billetAvionFile.name);
      }
      if (reservationHotelFile) {
        formData.append('reservationHotel', reservationHotelFile, reservationHotelFile.name);
      }

      const response = await axios.post(`${API_BASE_URL}/validerDemande`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.data && response.data.idDemande) {
        setCreatedDemandeId(response.data.idDemande);
      }

      setShowSuccessScreen(true);
    } catch (error) {
      console.error('Erreur lors de la validation de la demande:', error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (reste sur le même formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateArrivee('');
    setDateRetour('');
    setButVoyage('');
    setButVoyagePrecision('');
    setBilletAvionFile(null);
    setReservationHotelFile(null);
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setSliderValue(false);
  };

  // Juste une fonction vide pour le onClick si disabled
  const disabledPressHandler = () => {};

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt="Flag"
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              Demande enregistree avec succes !
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Votre demande de visa pour {title} a bien ete enregistree.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors société et hors Legrand) */}
              {isClientSociete === false && !hideFeesForLegrand && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  Payer en ligne
                </Button>
              )}

              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                Creer une nouvelle demande
              </Button>

              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                Voir mes demandes
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      sx={{ backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', minHeight: '100vh', py: 4 }}
    >
      <Container maxWidth="md">
        <Box sx={{ backgroundColor: '#fff', p: 3, borderRadius: 2, boxShadow: 2 }}>
          {/* En-tête du formulaire */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box component="img" src={urlFlag} alt="Flag" sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }} />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              Formulaire pour {title}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {validite}
              <br />
              {informations}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              Retour
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur si non en mode édition */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">Sélectionner un voyageur</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label="Sélectionner un voyageur"
                  onChange={(e: SelectChangeEvent) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map(v => (
                    <MenuItem key={v.id} value={v.id.toString()}>
                      {v.nom} {v.prenom}
                      {v.numeropax ? ` - Passeport: ${v.numeropax}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      informations,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#1976d2',
                    borderColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#1565c0'
                    }
                  }}
                >
                  Créer un nouveau voyageur
                </Button>
              </Box>
            </>
          )}

          {/* Avatar du voyageur sélectionné */}
          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt="Avatar"
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          {/* Dates du séjour */}
          <TextField
            label="Date d'arrivée à Singapour (jj/mm/aaaa)"
            placeholder="jj/mm/aaaa"
            value={dateArrivee}
            onChange={(e) => setDateArrivee(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Date de retour de Singapour (jj/mm/aaaa)"
            placeholder="jj/mm/aaaa"
            value={dateRetour}
            onChange={(e) => setDateRetour(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          {/* But du voyage */}
          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel id="but-voyage-label">But du voyage</InputLabel>
            <Select
              labelId="but-voyage-label"
              id="but-voyage-select"
              value={butVoyage}
              label="But du voyage"
              onChange={(e: SelectChangeEvent) => setButVoyage(e.target.value)}
            >
              {BUTS_VOYAGE.map((but) => (
                <MenuItem key={but} value={but}>
                  {but}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {butVoyage === 'Autre' && (
            <TextField
              label="Précisez le but du voyage"
              placeholder="Ex : congrès médical, visite familiale..."
              value={butVoyagePrecision}
              onChange={(e) => setButVoyagePrecision(e.target.value)}
              fullWidth
              margin="normal"
            />
          )}

          {/* Documents à télécharger */}
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Documents à télécharger
          </Typography>

          <FileDropZone
            label="Billet d'avion"
            file={billetAvionFile}
            onFileSelected={setBilletAvionFile}
            required
          />
          <FileDropZone
            label="Réservation d'hôtel"
            file={reservationHotelFile}
            onFileSelected={setReservationHotelFile}
            required
          />

          {/* Conditions générales de vente */}
          <ConditionsGeneralesVente accepte={sliderValue} onChange={setSliderValue} />

          {/* Récapitulatif des frais */}
          {!hideFeesForLegrand && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography variant="body1">Frais de visa : {prixconsulat}€</Typography>
              <Typography variant="body1">Frais de service : {prixintervention}€</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                Total : {total}€
              </Typography>
            </Box>
          )}

          {/* État du formulaire (incomplet / complet) */}
          <Box
            sx={{
              my: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc',
            }}
          >
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  Formulaire incomplet.
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">Formulaire complet</Typography>
            )}
          </Box>

          {/* Bouton de validation */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                Valider ma demande
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormSingapourECard;
